// src/pages/member/MemberResourcesMarketingTools.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";

const pageBg = "#f9fbfe";
const sectionHeaderBg = "#c4d8ec";
const sectionHeaderBorder = "#4e81c4";
const linkBlue = "#005695";

const getFileIcon = (type) => {
  const t = (type || "").toLowerCase();
  if (t === "pdf") return <PictureAsPdfIcon sx={{ fontSize: 18, color: "#fff" }} />;
  if (t === "jpg" || t === "jpeg" || t === "png" || t === "svg")
    return <ImageIcon sx={{ fontSize: 18, color: "#fff" }} />;
  return <DescriptionIcon sx={{ fontSize: 18, color: "#fff" }} />;
};

/* ---------- Data ---------- */

const sections = [
  {
    title: "Marketing Toolkit",
    groups: [
      {
        heading: "Welcome Pack",
        description:
          "A handy starter pack you can share with your team to explain what RECC membership means and how to present it to consumers.",
        items: [
          {
            title: "Welcome Pack",
            description: "Overview and key information about your RECC membership.",
            type: "", // update when you know the actual file type
            href: "/assets/marketing/welcome-pack",
          },
        ],
      },
    ],
  },
  {
    title: "Logos",
    groups: [
      {
        heading: "About the logos",
        description:
          "Here you will find the RECC logo and CTSI approved Code logo in different formats, making it easy to use them across your website, documents, vehicles, and marketing materials, helping you clearly show customers that you follow high consumer protection standards.",
        items: [
          {
            title: "Guidance on the use of the RECC and CTSI Logo (pdf)",
            description:
              "Important guidance on where and how the RECC and CTSI logos may be used.",
            type: "pdf",
            href: "/assets/marketing/logos/guidance-use-of-recc-ctsi-logo.pdf",
          },
        ],
      },
      {
        heading: "RECC and CTSI joint logos (jpg files)",
        items: [
          {
            title: "Horizontal (CMYK)",
            type: "jpg",
            href: "/assets/marketing/logos/jpg/horizontal-cmyk.jpg",
          },
          {
            title: "Horizontal (RGB)",
            type: "jpg",
            href: "/assets/marketing/logos/jpg/horizontal-rgb.jpg",
          },
          {
            title: "Horizontal Black (CMYK)",
            type: "jpg",
            href: "/assets/marketing/logos/jpg/horizontal-black-cmyk.jpg",
          },
          {
            title: "Horizontal Black (RGB)",
            type: "jpg",
            href: "/assets/marketing/logos/jpg/horizontal-black-rgb.jpg",
          },
          {
            title: "Vertical (CMYK)",
            type: "jpg",
            href: "/assets/marketing/logos/jpg/vertical-cmyk.jpg",
          },
          {
            title: "Vertical (RGB)",
            type: "jpg",
            href: "/assets/marketing/logos/jpg/vertical-rgb.jpg",
          },
          {
            title: "Vertical Black (CMYK)",
            type: "jpg",
            href: "/assets/marketing/logos/jpg/vertical-black-cmyk.jpg",
          },
          {
            title: "Vertical Black (RGB)",
            type: "jpg",
            href: "/assets/marketing/logos/jpg/vertical-black-rgb.jpg",
          },
        ],
      },
      {
        heading: "RECC and CTSI joint logos (transparent png files)",
        items: [
          {
            title: "Horizontal (RGB)",
            type: "png",
            href: "/assets/marketing/logos/png/horizontal-rgb.png",
          },
          {
            title: "Horizontal Black (RGB)",
            type: "png",
            href: "/assets/marketing/logos/png/horizontal-black-rgb.png",
          },
          {
            title: "Horizontal White (RGB)",
            type: "png",
            href: "/assets/marketing/logos/png/horizontal-white-rgb.png",
          },
          {
            title: "Vertical (RGB)",
            type: "png",
            href: "/assets/marketing/logos/png/vertical-rgb.png",
          },
          {
            title: "Vertical Black (RGB)",
            type: "png",
            href: "/assets/marketing/logos/png/vertical-black-rgb.png",
          },
          {
            title: "Vertical White (RGB)",
            type: "png",
            href: "/assets/marketing/logos/png/vertical-white-rgb.png",
          },
        ],
      },
      {
        heading: "RECC and CTSI joint logos (vector files)",
        items: [
          {
            title: "Horizontal (CMYK)",
            type: "svg",
            href: "/assets/marketing/logos/vector/horizontal-cmyk.svg",
          },
          {
            title: "Horizontal (RGB)",
            type: "svg",
            href: "/assets/marketing/logos/vector/horizontal-rgb.svg",
          },
          {
            title: "Horizontal Black (CMYK)",
            type: "svg",
            href: "/assets/marketing/logos/vector/horizontal-black-cmyk.svg",
          },
          {
            title: "Horizontal Black (RGB)",
            type: "svg",
            href: "/assets/marketing/logos/vector/horizontal-black-rgb.svg",
          },
          {
            title: "Horizontal White",
            type: "svg",
            href: "/assets/marketing/logos/vector/horizontal-white.svg",
          },
          {
            title: "Vertical (CMYK)",
            type: "svg",
            href: "/assets/marketing/logos/vector/vertical-cmyk.svg",
          },
          {
            title: "Vertical (RGB)",
            type: "svg",
            href: "/assets/marketing/logos/vector/vertical-rgb.svg",
          },
          {
            title: "Vertical Black (CMYK)",
            type: "svg",
            href: "/assets/marketing/logos/vector/vertical-black-cmyk.svg",
          },
          {
            title: "Vertical Black (RGB)",
            type: "svg",
            href: "/assets/marketing/logos/vector/vertical-black-rgb.svg",
          },
          {
            title: "Vertical White",
            type: "svg",
            href: "/assets/marketing/logos/vector/vertical-white.svg",
          },
        ],
      },
    ],
  },
  {
    title: "RECC Leaflets",
    groups: [
      {
        heading: "Consumer leaflets",
        description:
          "Make sure you tell your customers what your RECC membership means to them. High resolution versions of our consumer leaflets are available to download below – they can be shared digitally or are suitable for professional printing.",
        items: [
          {
            title: "RECC Consumer Leaflet (pdf)",
            type: "pdf",
            href: "/assets/marketing/leaflets/recc-consumer-leaflet.pdf",
          },
          {
            title: "Top Tips for Consumers (pdf)",
            type: "pdf",
            href: "/assets/marketing/leaflets/top-tips-for-consumers.pdf",
          },
        ],
        note: "Please note that the content of these leaflets must not be altered in any way.",
      },
    ],
  },
  {
    title: "Social media content",
    groups: [
      {
        heading: "Ready-made content",
        description:
          "Use these assets to promote your RECC membership consistently across your digital channels.",
        items: [
          {
            title: "E-signatures",
            description: "Branded email signatures highlighting your RECC membership.",
            type: "",
            href: "/assets/marketing/social/e-signatures",
          },
          {
            title: "Website banners",
            description:
              "High quality banners for your website to showcase your RECC accreditation.",
            type: "",
            href: "/assets/marketing/social/website-banners",
          },
          {
            title: "Wording for social media posts",
            description:
              "Suggested copy to help you share your RECC membership on social platforms.",
            type: "pdf",
            href: "/assets/marketing/social/social-post-wording.pdf",
          },
        ],
      },
    ],
  },
  {
    title: "Van stickers",
    groups: [
      {
        heading: "Display your accreditation on the road",
        description:
          "As a RECC member, you can get free van stickers to proudly display your accreditation on your vehicles and increase visibility while on the road.",
        items: [
          {
            title: "Request van stickers",
            description:
              "Drop us an email and let us know how many stickers you’d like, and we can post these to you.",
            type: "",
            href: "mailto:membership@recc.org.uk",
          },
        ],
      },
    ],
  },
];

/* ---------- UI components ---------- */

function SectionCard({ section }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #dde4f0",
        mb: 4,
      }}
    >
      <Box
        sx={{
          backgroundColor: sectionHeaderBg,
          borderTop: `4px solid ${sectionHeaderBorder}`,
          px: 3,
          py: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1d2736" }}>
          {section.title}
        </Typography>
      </Box>

      <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>
        {section.groups.map((group, groupIndex) => (
          <Box
            key={group.heading}
            sx={{ mb: groupIndex < section.groups.length - 1 ? 3 : 0 }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: group.description ? 0.5 : 1, color: "#111827" }}
            >
              {group.heading}
            </Typography>

            {group.description && (
              <Typography
                variant="body2"
                sx={{ mb: 1.5, color: "#4b5563", maxWidth: 900 }}
              >
                {group.description}
              </Typography>
            )}

            <List disablePadding>
              {group.items.map((item, itemIndex) => (
                <React.Fragment key={item.title}>
                  <ListItemButton
                    component="a"
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      alignItems: "flex-start",
                      px: 0,
                      "&:hover .linkText": { textDecoration: "underline" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 48, mt: 0.5 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: sectionHeaderBorder,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {getFileIcon(item.type)}
                      </Box>
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Typography
                          className="linkText"
                          sx={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: linkBlue,
                          }}
                        >
                          {item.title}
                        </Typography>
                      }
                      secondary={
                        item.description && (
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.5, color: "#4a4f59", lineHeight: 1.5 }}
                          >
                            {item.description}
                          </Typography>
                        )
                      }
                    />
                  </ListItemButton>

                  {itemIndex < group.items.length - 1 && (
                    <Divider sx={{ my: 1.75, borderColor: "#edf1f7" }} />
                  )}
                </React.Fragment>
              ))}
            </List>

            {group.note && (
              <Typography
                variant="body2"
                sx={{ mt: 1, color: "#6b7280", fontStyle: "italic" }}
              >
                {group.note}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

export default function MemberResourcesMarketingTools() {
  return (
    <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
          Home &gt; Member Resources &gt; Marketing Tools
        </Typography>

        {/* Page title + intro */}
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: "#111827" }}>
          Marketing Tools
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: "#4b5563" }}>
          Use these marketing resources to promote your RECC membership and clearly
          communicate the high consumer protection standards you follow.
        </Typography>

        {sections.map((section) => (
          <SectionCard key={section.title} section={section} />
        ))}
      </Container>
    </Box>
  );
}
